import React from 'react';

/**
 * Renders blog content with support for YouTube video embeds and proper image handling
 * Supports both full YouTube URLs and shorthand [youtube:VIDEO_ID] tags
 */
const BlogContent = ({ content }) => {
  if (!content) return null;

  // Process content to handle YouTube embeds and images
  const processContent = (html) => {
    let processed = html;

    // Pattern 1: [youtube:VIDEO_ID] shorthand
    processed = processed.replace(
      /\[youtube:([a-zA-Z0-9_-]+)\]/g,
      (match, videoId) => {
        return `<div class="youtube-embed my-6"><div class="relative w-full" style="padding-bottom: 56.25%;"><iframe src="https://www.youtube.com/embed/${videoId}" title="YouTube video" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen class="absolute top-0 left-0 w-full h-full rounded-lg"></iframe></div></div>`;
      }
    );

    // Pattern 2: Full YouTube URLs (watch?v= format)
    processed = processed.replace(
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)(?:&[^\s<]*)?/g,
      (match, videoId) => {
        return `<div class="youtube-embed my-6"><div class="relative w-full" style="padding-bottom: 56.25%;"><iframe src="https://www.youtube.com/embed/${videoId}" title="YouTube video" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen class="absolute top-0 left-0 w-full h-full rounded-lg"></iframe></div></div>`;
      }
    );

    // Pattern 3: Short YouTube URLs (youtu.be format)
    processed = processed.replace(
      /(?:https?:\/\/)?youtu\.be\/([a-zA-Z0-9_-]+)(?:\?[^\s<]*)?/g,
      (match, videoId) => {
        return `<div class="youtube-embed my-6"><div class="relative w-full" style="padding-bottom: 56.25%;"><iframe src="https://www.youtube.com/embed/${videoId}" title="YouTube video" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen class="absolute top-0 left-0 w-full h-full rounded-lg"></iframe></div></div>`;
      }
    );

    // Enhance image tags with proper styling
    processed = processed.replace(
      /<img([^>]*)>/gi,
      (match, attrs) => {
        // Check if already has class
        if (attrs.includes('class=')) {
          return `<img${attrs.replace(/class="([^"]*)"/, 'class="$1 max-w-full h-auto rounded-lg my-4"')}>`;
        }
        return `<img${attrs} class="max-w-full h-auto rounded-lg my-4" loading="lazy">`;
      }
    );

    // Handle tables - add responsive wrapper and styling
    processed = processed.replace(
      /<table([^>]*)>/gi,
      '<div class="overflow-x-auto my-6"><table$1 class="min-w-full border-collapse">'
    );
    processed = processed.replace(/<\/table>/gi, '</table></div>');

    // Style table cells if not already styled
    processed = processed.replace(
      /<th(?![^>]*class=)([^>]*)>/gi,
      '<th$1 class="border border-gray-600 bg-gray-800 px-4 py-2 text-left font-semibold text-white">'
    );
    processed = processed.replace(
      /<td(?![^>]*class=)([^>]*)>/gi,
      '<td$1 class="border border-gray-700 px-4 py-2 text-gray-300">'
    );

    // Style lists if not already styled
    processed = processed.replace(
      /<ul(?![^>]*class=)([^>]*)>/gi,
      '<ul$1 class="list-disc list-inside space-y-2 my-4 text-gray-300">'
    );
    processed = processed.replace(
      /<ol(?![^>]*class=)([^>]*)>/gi,
      '<ol$1 class="list-decimal list-inside space-y-2 my-4 text-gray-300">'
    );

    // Style headings if not already styled
    processed = processed.replace(
      /<h1(?![^>]*class=)([^>]*)>/gi,
      '<h1$1 class="text-3xl font-bold text-white mt-8 mb-4">'
    );
    processed = processed.replace(
      /<h2(?![^>]*class=)([^>]*)>/gi,
      '<h2$1 class="text-2xl font-bold text-white mt-6 mb-3">'
    );
    processed = processed.replace(
      /<h3(?![^>]*class=)([^>]*)>/gi,
      '<h3$1 class="text-xl font-semibold text-white mt-5 mb-2">'
    );

    // Style paragraphs
    processed = processed.replace(
      /<p(?![^>]*class=)([^>]*)>/gi,
      '<p$1 class="text-gray-300 leading-relaxed mb-4">'
    );

    // Style links
    processed = processed.replace(
      /<a(?![^>]*class=)([^>]*)>/gi,
      '<a$1 class="text-orange-400 hover:text-orange-300 underline">'
    );

    // Convert double newlines to paragraph breaks, single newlines to <br/>
    // Only if content doesn't already have proper HTML structure
    if (!processed.includes('<p>') && !processed.includes('<div>')) {
      processed = processed
        .split(/\n\n+/)
        .map(para => para.trim())
        .filter(para => para)
        .map(para => `<p class="text-gray-300 leading-relaxed mb-4">${para.replace(/\n/g, '<br/>')}</p>`)
        .join('');
    } else {
      // Just handle stray newlines
      processed = processed.replace(/([^>])\n([^<])/g, '$1<br/>$2');
    }

    return processed;
  };

  return (
    <div 
      className="blog-content"
      dangerouslySetInnerHTML={{ __html: processContent(content) }}
    />
  );
};

export default BlogContent;
